import React, { ReactNode } from 'react';
import { Tooltip } from 'antd';
import StarIcon from '../icons/StarIcon';

export type AnalyticsChartHeaderPill = {
	label: string;
	color: string;
	bgColor: string;
	maxWidth?: number;
};

export type AnalyticsChartHeaderProps = {
	/** Optional pill rendered before the label (e.g., priority chip). */
	pill?: AnalyticsChartHeaderPill | null;
	/** Main label text (e.g., "Avg Approval Time", "Spend", "WO Performance"). */
	label: string;
	/**
	 * Already-formatted summary value displayed after the label (e.g., "1d 5h", "$12,345").
	 * When provided the header shows "{label}: {value}". When omitted only the label is shown.
	 */
	value?: string;
	/** Hover-tooltip text for the star info marker. When omitted the star is not rendered. */
	infoTooltip?: string;
	/** Optional delta indicator (arrow + percent). Rendered to the right of the value. */
	delta?: ReactNode;
	/** Tooltip shown when hovering the label/value group. Defaults to "{label}: {value}". */
	titleTooltip?: string;
	/** Optional content rendered after the label/value block (e.g., a filter chip). */
	after?: ReactNode;
};

const AnalyticsChartHeader = ({
	pill,
	label,
	value,
	infoTooltip,
	delta,
	titleTooltip,
	after,
}: AnalyticsChartHeaderProps) => {
	const labelTooltip = titleTooltip || (value ? `${label}: ${value}` : '');
	const labelBlock = (
		<div className="flex min-w-0 items-baseline gap-1">
			<span className="h5medium text-character-title/85 min-w-0 truncate">
				{value ? `${label}:` : label}
			</span>
			{value ? (
				<span className="h5medium text-character-title/85 shrink-0 whitespace-nowrap">{value}</span>
			) : null}
			{infoTooltip ? (
				<Tooltip title={infoTooltip}>
					<span className="relative -top-[8px] -ml-[4px] inline-flex shrink-0 self-center leading-none">
						<StarIcon />
					</span>
				</Tooltip>
			) : null}
			{delta ? <span className="shrink-0">{delta}</span> : null}
		</div>
	);
	return (
		<div className="flex min-w-0 flex-nowrap items-center gap-3">
			{pill ? (
				<div className="inline-block min-w-0 max-w-full shrink-0 align-middle">
					<Tooltip title={pill.label}>
						<span
							className="inline-block min-w-0 max-w-full truncate rounded-2xl px-3 py-1 align-middle text-sm font-medium leading-[22px]"
							style={{
								color: pill.color,
								backgroundColor: pill.bgColor,
								maxWidth: pill.maxWidth || 140,
							}}
						>
							{pill.label}
						</span>
					</Tooltip>
				</div>
			) : null}
			{labelTooltip ? <Tooltip title={labelTooltip}>{labelBlock}</Tooltip> : labelBlock}
			{after}
		</div>
	);
};

export default AnalyticsChartHeader;
